import React from 'react'
import { Badge, Card, Col, Form, Input, Row, Select, Space, Typography, Divider, Tooltip, Alert, Modal } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faLocationDot,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faMapPin,
  faBuilding,
  faGlobe,
  faInfoCircle,
  faCheckCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons'
import { hexToRGBA, specialFieldValidation } from '../../../../lib/utils'
import {
  CLINIC_FORM_CATEGORY_ICONS,
  CLINIC_FORM_CATEGORY_COLORS,
  CLINIC_FORM_CATEGORY_LABELS
} from '../../config/form-categories'
import Templates from '../../utils/templates'
import GoogleAddress from '../../../../reusable-components/GoogleAddress'
import { clinicTabsEnums } from '../../config/clinic-tabs-enums'

const { Title, Text, Paragraph } = Typography

export const createLocationContactTab = ({
  token,
  unfilledFields,
  locationContactForm,
  specialFormState,
  setSpecialFormState,
  data,
  handleGetAddress,
  physicalAddress,
  handleFormSubmit,
  recordData,
  handleValuesChange,
  basicInfoValid
}) => {
  return {
    key: clinicTabsEnums.LOCATION_CONTACT,
    disabled: !basicInfoValid,
    label: (
      <span>
        <FontAwesomeIcon
          icon={CLINIC_FORM_CATEGORY_ICONS.LOCATION_AND_CONTACT}
          style={{ marginRight: '8px', color: CLINIC_FORM_CATEGORY_COLORS.LOCATION_AND_CONTACT }}
        />
        {CLINIC_FORM_CATEGORY_LABELS.LOCATION_AND_CONTACT}
        <Badge count={unfilledFields[2]} style={{ marginLeft: '8px', backgroundColor: token.colorError }} />
      </span>
    ),
    children: (
      <Form
        onFinish={handleFormSubmit}
        onFinishFailed={(errorInfo) => {
          Modal.error({
            title: 'Form Validation Error',
            content: errorInfo.errorFields[0].errors[0]
          })
        }}
        name='form'
        form={locationContactForm}
        initialValues={{
          ...Templates.dataTemplate(),
          ...recordData
        }}
        layout='vertical'
        onValuesChange={(values, fullForm) => {
          handleValuesChange(fullForm)
        }}
      >
        <div style={{ padding: '24px 0', background: hexToRGBA(token.colorBgLayout, 0.3) }}>
          <Row gutter={[32, 32]}>
            {/* Location Column */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space size={12}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        backgroundColor: hexToRGBA(token.colorPrimary, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: token.colorPrimary, fontSize: '18px' }} />
                    </div>
                    <div>
                      <Text strong style={{ fontSize: '18px', display: 'block' }}>
                        Location Information
                      </Text>
                      <Text type='secondary' style={{ fontSize: '14px' }}>
                        Set your facility's physical location
                      </Text>
                    </div>
                  </Space>
                }
                bordered={false}
                style={{
                  borderRadius: '16px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f0f8ff 100%)',
                  border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`,
                  height: '100%'
                }}
                bodyStyle={{ padding: '32px' }}
                headStyle={{
                  borderBottom: 'none',
                  backgroundColor: 'transparent',
                  padding: '24px 32px 0 32px'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                  {/* Province Selection */}
                  <div>
                    <Title level={5} style={{ margin: '0 0 16px 0', color: token.colorPrimary }}>
                      <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '8px' }} />
                      Geographic Location
                    </Title>

                    <Form.Item
                      name={Templates.dataModel.province.key}
                      label={
                        <Space>
                          <Text strong style={{ fontSize: '15px' }}>
                            {Templates.dataModel.province.label}
                          </Text>
                          <Tooltip title='Select the province where your facility operates'>
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              style={{ color: token.colorPrimary, fontSize: '12px' }}
                            />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          required: Templates.dataModel.province.required,
                          message: Templates.dataModel.province.validationMsg
                        }
                      ]}
                    >
                      <Select
                        showSearch
                        size='large'
                        placeholder='- Select a Province -'
                        optionFilterProp='children'
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={Templates.dataModel.province.options}
                        suffixIcon={<FontAwesomeIcon icon={faMapPin} style={{ color: token.colorPrimary }} />}
                        style={{
                          width: '100%',
                          borderRadius: '8px'
                        }}
                      />
                    </Form.Item>
                  </div>

                  <Divider style={{ margin: '8px 0', borderColor: hexToRGBA(token.colorBorder, 0.3) }} />

                  {/* Address Search */}
                  <div>
                    <Title level={5} style={{ margin: '0 0 16px 0', color: token.colorPrimary }}>
                      <FontAwesomeIcon icon={faLocationDot} style={{ marginRight: '8px' }} />
                      Find Your Facility
                    </Title>

                    <Paragraph style={{ marginBottom: '20px', color: token.colorTextSecondary }}>
                      Start typing your facility's address to find the exact location using Google Maps.
                    </Paragraph>

                    <Form.Item
                      name={Templates.dataModel.address.key}
                      label={
                        <Space>
                          <Text strong style={{ fontSize: '15px' }}>
                            {Templates.dataModel.address.label}
                          </Text>
                          <Tooltip title='Use Google search to find your exact facility address'>
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              style={{ color: token.colorPrimary, fontSize: '12px' }}
                            />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          required: Templates.dataModel.address.required,
                          message: Templates.dataModel.address.validationMsg
                        }
                      ]}
                    >
                      <GoogleAddress
                        selectProps={{
                          placeholder: data.address || Templates.dataModel.address.placeholder,
                          size: 'large',
                          style: {
                            width: '100%',
                            borderRadius: '8px',
                            fontSize: '16px'
                          },
                          onChange: async (e) => {
                            if (e.label) {
                              handleGetAddress(e.label)

                              // Get coordinates from place_id
                              try {
                                const { geocodeByPlaceId } = await import('react-google-places-autocomplete')
                                const results = await geocodeByPlaceId(e.value.place_id)

                                if (results && results.length > 0) {
                                  const location = results[0].geometry.location
                                  const coordinates = {
                                    type: 'Point',
                                    coordinates: [location.lng(), location.lat()] // [lng, lat] for MongoDB
                                  }

                                  locationContactForm.setFieldsValue({
                                    [Templates.dataModel.address.key]: e.label,
                                    [Templates.dataModel.place_id.key]: e.value.place_id,
                                    geolocation: {
                                      geoType: 'Point',
                                      coordinates: [location.lng(), location.lat()]
                                    }
                                  })

                                  handleValuesChange({
                                    ...locationContactForm.getFieldsValue(),
                                    address: e.label,
                                    place_id: e.value.place_id,
                                    geolocation: {
                                      geoType: 'Point',
                                      coordinates: [location.lng(), location.lat()]
                                    }
                                  })
                                } else {
                                  console.warn('❌ No coordinates found for place_id:', e.value.place_id)
                                }
                              } catch (error) {
                                console.error('❌ Error getting coordinates:', error)

                                // Still save the address and place_id even if coordinates fail
                                locationContactForm.setFieldsValue({
                                  [Templates.dataModel.address.key]: e.label,
                                  [Templates.dataModel.place_id.key]: e.value.place_id
                                })
                                handleValuesChange({
                                  ...locationContactForm.getFieldsValue(),
                                  address: e.label,
                                  place_id: e.value.place_id
                                })
                              }
                            }
                          }
                        }}
                      />
                    </Form.Item>
                    <Form.Item name={Templates.dataModel.geolocation.key} hidden />
                  </div>

                  {/* Physical Address Display */}
                  {physicalAddress && (
                    <Card
                      size='small'
                      style={{
                        borderRadius: '12px',
                        border: `2px solid ${hexToRGBA(token.colorSuccess, 0.2)}`,
                        backgroundColor: hexToRGBA(token.colorSuccess, 0.05),
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                      }}
                      bodyStyle={{ padding: '20px' }}
                    >
                      <div style={{ marginBottom: '12px' }}>
                        <Space>
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            style={{ color: token.colorSuccess, fontSize: '16px' }}
                          />
                          <Text strong style={{ color: token.colorSuccess, fontSize: '16px' }}>
                            Address Confirmed
                          </Text>
                        </Space>
                      </div>

                      <Form.Item
                        name={Templates.dataModel.physicalAddress.key}
                        label={
                          <Space>
                            <FontAwesomeIcon icon={faBuilding} style={{ color: token.colorSuccess }} />
                            <Text strong>{Templates.dataModel.physicalAddress.label}</Text>
                          </Space>
                        }
                        style={{ marginBottom: 0 }}
                        rules={[
                          {
                            required: Templates.dataModel.physicalAddress.required,
                            message: Templates.dataModel.physicalAddress.validationMsg
                          }
                        ]}
                      >
                        <Input.TextArea
                          rows={3}
                          readOnly
                          style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${hexToRGBA(token.colorSuccess, 0.3)}`,
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        />
                      </Form.Item>
                    </Card>
                  )}

                  {/* Location Tips */}
                  <Alert
                    message='Location Tips'
                    description={
                      <ul style={{ paddingLeft: '16px', margin: '8px 0 0 0' }}>
                        <li>Ensure the address is easily accessible to patients</li>
                        <li>Verify the location has adequate parking facilities</li>
                        <li>Consider public transportation accessibility</li>
                      </ul>
                    }
                    type='info'
                    showIcon
                    icon={<FontAwesomeIcon icon={faMapMarkerAlt} />}
                    style={{
                      borderRadius: '8px',
                      border: `1px solid ${hexToRGBA(token.colorInfo, 0.3)}`
                    }}
                  />
                </div>
              </Card>
            </Col>

            {/* Contact Column */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space size={12}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        backgroundColor: hexToRGBA(token.colorSuccess, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faPhone} style={{ color: token.colorSuccess, fontSize: '18px' }} />
                    </div>
                    <div>
                      <Text strong style={{ fontSize: '18px', display: 'block' }}>
                        Contact Information
                      </Text>
                      <Text type='secondary' style={{ fontSize: '14px' }}>
                        How patients and staff can reach you
                      </Text>
                    </div>
                  </Space>
                }
                bordered={false}
                style={{
                  borderRadius: '16px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f0fff4 100%)',
                  border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`,
                  height: '100%'
                }}
                bodyStyle={{ padding: '32px' }}
                headStyle={{
                  borderBottom: 'none',
                  backgroundColor: 'transparent',
                  padding: '24px 32px 0 32px'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                  <div>
                    <Title level={5} style={{ margin: '0 0 20px 0', color: token.colorSuccess }}>
                      <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '8px' }} />
                      Communication Details
                    </Title>

                    {/* Email */}
                    <Form.Item
                      name={Templates.dataModel.email.key}
                      label={
                        <Space>
                          <Text strong style={{ fontSize: '15px' }}>
                            {Templates.dataModel.email.label}
                          </Text>
                          <Tooltip title='Primary email for official communications and appointments'>
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              style={{ color: token.colorSuccess, fontSize: '12px' }}
                            />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          required: Templates.dataModel.email.required,
                          message: Templates.dataModel.email.validationMsg
                        },
                        {
                          type: 'email',
                          message:
                            'Please enter a valid email address in the correct format (e.g., clinic@domain.co.za). This will be used for official communications and appointment confirmations.'
                        }
                      ]}
                      style={{ marginBottom: '24px' }}
                    >
                      <Input
                        type='email'
                        size='large'
                        placeholder={Templates.dataModel.email.placeholder}
                        prefix={
                          <FontAwesomeIcon
                            icon={faEnvelope}
                            style={{ color: token.colorSuccess, marginRight: '8px' }}
                          />
                        }
                        style={{
                          borderRadius: '8px',
                          fontSize: '16px',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>

                    {/* Phone Number */}
                    <Form.Item
                      name={Templates.dataModel.phoneNumber.key}
                      label={
                        <Space>
                          <Text strong style={{ fontSize: '15px' }}>
                            {Templates.dataModel.phoneNumber.label}
                          </Text>
                          <Tooltip title='Main contact number for patients and queries'>
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              style={{ color: token.colorSuccess, fontSize: '12px' }}
                            />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          required: Templates.dataModel.phoneNumber.required,
                          message: Templates.dataModel.phoneNumber.validationMsg
                        }
                      ]}
                    >
                      <Input
                        size='large'
                        onChange={(e) => {
                          specialFieldValidation(locationContactForm, specialFormState, setSpecialFormState, {
                            key: 'phoneNumber',
                            event: e,
                            validationConfig: {
                              letters: { allowed: false, onlyCaps: false },
                              numbers: true,
                              spaces: false,
                              specialChars: false
                            }
                          })
                        }}
                        maxLength={50}
                        placeholder={Templates.dataModel.phoneNumber.placeholder}
                        prefix={
                          <FontAwesomeIcon icon={faPhone} style={{ color: token.colorSuccess, marginRight: '8px' }} />
                        }
                        style={{
                          borderRadius: '8px',
                          fontSize: '16px',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>
                  </div>

                  <Divider style={{ margin: '8px 0', borderColor: hexToRGBA(token.colorBorder, 0.3) }} />

                  {/* Contact Best Practices */}
                  <Card
                    size='small'
                    style={{
                      borderRadius: '12px',
                      border: `2px solid ${hexToRGBA(token.colorWarning, 0.2)}`,
                      backgroundColor: hexToRGBA(token.colorWarning, 0.05),
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <Space direction='vertical' style={{ width: '100%' }} size='small'>
                      <div style={{ marginBottom: '12px' }}>
                        <Space>
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{ color: token.colorWarning, fontSize: '16px' }}
                          />
                          <Text strong style={{ color: token.colorWarning, fontSize: '16px' }}>
                            Contact Best Practices
                          </Text>
                        </Space>
                      </div>

                      <div style={{ paddingLeft: '8px' }}>
                        <div style={{ marginBottom: '8px' }}>
                          <Text strong style={{ fontSize: '14px', color: token.colorTextHeading }}>
                            Email Guidelines:
                          </Text>
                        </div>
                        <ul style={{ paddingLeft: '16px', margin: '0 0 16px 0' }}>
                          <li>
                            <Text type='secondary' style={{ fontSize: '13px' }}>
                              Use a professional domain (avoid free email services)
                            </Text>
                          </li>
                          <li>
                            <Text type='secondary' style={{ fontSize: '13px' }}>
                              Monitor regularly for appointment requests
                            </Text>
                          </li>
                          <li>
                            <Text type='secondary' style={{ fontSize: '13px' }}>
                              Set up auto-replies for after-hours inquiries
                            </Text>
                          </li>
                        </ul>

                        <div style={{ marginBottom: '8px' }}>
                          <Text strong style={{ fontSize: '14px', color: token.colorTextHeading }}>
                            Phone Guidelines:
                          </Text>
                        </div>
                        <ul style={{ paddingLeft: '16px', margin: '0' }}>
                          <li>
                            <Text type='secondary' style={{ fontSize: '13px' }}>
                              Provide a direct line when possible
                            </Text>
                          </li>
                          <li>
                            <Text type='secondary' style={{ fontSize: '13px' }}>
                              Include area code for clarity
                            </Text>
                          </li>
                          <li>
                            <Text type='secondary' style={{ fontSize: '13px' }}>
                              Consider separate numbers for appointments vs. emergencies
                            </Text>
                          </li>
                        </ul>
                      </div>
                    </Space>
                  </Card>

                  {/* Security Notice */}
                  <Alert
                    message='Privacy & Security'
                    description='Your contact information will be used for official communications only and protected according to healthcare privacy regulations.'
                    type='success'
                    showIcon
                    icon={<FontAwesomeIcon icon={faCheckCircle} />}
                    style={{
                      borderRadius: '8px',
                      border: `1px solid ${hexToRGBA(token.colorSuccess, 0.3)}`
                    }}
                  />

                  {/* Hidden field */}
                  <Form.Item name={Templates.dataModel.place_id.key} hidden />
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </Form>
    )
  }
}
